import { drillLocationsEqual, isDrillLocationAncestor } from '../drill/drillLocation';
import { FOCUSED_REASON_KEYS } from './ancestryChips';
import { DRILLABLE_NODE_TYPES, DRILLABLE_STRUCTURAL_TYPES, type PackNode, type PackNodeVolume } from './packNodeTypes';
import type {
  DrillLocation,
  FocusedActivationKey,
  ExplorationSecondary,
  StructuralLocation,
  ViewMode,
} from '../../types/exploration';

export { DRILLABLE_NODE_TYPES, DRILLABLE_STRUCTURAL_TYPES };

export interface BubbleClickContext {
  secondary?: ExplorationSecondary;
  tagKey?: string;
}

function nodeChain(node: PackNode): PackNode[] {
  return (node.ancestors?.() ?? [node]).slice().reverse();
}

function withNodeGroup(structural: StructuralLocation, name: string): StructuralLocation {
  if (structural.kind !== 'cluster') return structural;
  return { kind: 'cluster', top: structural.top, nodeGroup: name };
}

function withInstance(structural: StructuralLocation, name: string): StructuralLocation {
  if (structural.kind === 'cluster') {
    return { kind: 'cluster', top: structural.top, nodeGroup: structural.nodeGroup, instanceId: name };
  }
  if (structural.kind === 'region') {
    return { kind: 'region', region: structural.region, instanceId: name };
  }
  if (structural.kind === 'tag') {
    return { kind: 'tag', tagValue: structural.tagValue, instanceId: name };
  }
  return structural;
}

/**
 * Structural location for a clicked node: start from the canonical current
 * location (hierarchy peels ancestors once drilled), then deepen with whatever
 * structural nodes remain in the pack ancestry (nodeGroup / instance / …).
 */
function structuralLocationForNode(
  node: PackNode,
  current: DrillLocation
): Pick<DrillLocation, 'account' | 'structural'> {
  let account: string | undefined = current.account;
  let structural: StructuralLocation = current.structural;

  for (const n of nodeChain(node)) {
    const { type, name } = n.data;
    if (!name || !DRILLABLE_STRUCTURAL_TYPES.has(type)) continue;

    if (type === 'account') {
      account = name;
    } else if (type === 'cluster') {
      structural = { kind: 'cluster', top: { kind: 'cluster', name } };
    } else if (type === 'ec2') {
      structural = { kind: 'cluster', top: { kind: 'ec2' } };
    } else if (type === 'unattached') {
      structural = { kind: 'cluster', top: { kind: 'unattached' } };
    } else if (type === 'nodeGroup') {
      structural = withNodeGroup(structural, name);
    } else if (type === 'instance') {
      structural = withInstance(structural, name);
    } else if (type === 'region') {
      structural = { kind: 'region', region: name };
    } else if (type === 'tag') {
      structural = { kind: 'tag', tagValue: name };
    }
  }

  return { account, structural };
}

/** Nearest enclosing focused-activation-category wrapper, if the node sits under one. */
function focusedActivationKeyForNode(node: PackNode): FocusedActivationKey | undefined {
  for (const n of nodeChain(node)) {
    if (n.data.type === 'status' && n.data.key && FOCUSED_REASON_KEYS.has(n.data.key as FocusedActivationKey)) {
      return n.data.key as FocusedActivationKey;
    }
  }
  return undefined;
}

export type BubbleClickAction =
  | { type: 'noop' }
  | { type: 'drillUp' }
  | { type: 'expandOverflow'; parentKey: string }
  | { type: 'selectVolume'; volume: PackNodeVolume }
  | { type: 'selectInstance'; instanceId: string; volumes: PackNodeVolume[] }
  | { type: 'drill'; location: DrillLocation };

/**
 * Current-root / self-hit → noop (clicking the drilled-in container must not
 * navigate back). Parent-ring (strict ancestor) → drill up. Only deepen /
 * lateral moves push a new visit.
 */
function drillOrNoop(next: DrillLocation, current: DrillLocation): BubbleClickAction {
  if (drillLocationsEqual(next, current)) return { type: 'noop' };
  return isDrillLocationAncestor(next, current)
    ? { type: 'drillUp' }
    : { type: 'drill', location: next };
}

/** Pure resolver for bubble circle click → navigation action. */
export function resolveBubbleClickAction(
  location: DrillLocation,
  view: ViewMode,
  node: PackNode,
  _ctx: BubbleClickContext = {}
): BubbleClickAction {
  const data = node.data;

  if (data._isOverflow) {
    return data._overflowParentKey
      ? { type: 'expandOverflow', parentKey: data._overflowParentKey }
      : { type: 'noop' };
  }

  if (data.type === 'volume' && data.volume) {
    return { type: 'selectVolume', volume: data.volume };
  }

  if (data.type === 'instance') {
    return { type: 'selectInstance', instanceId: data.name ?? '', volumes: data._volumes ?? [] };
  }

  const { account, structural } = structuralLocationForNode(node, location);

  if (data.type === 'status') {
    if (data.key && view === 'activation' && FOCUSED_REASON_KEYS.has(data.key as FocusedActivationKey)) {
      return drillOrNoop(
        { account, structural, focusedActivationKey: data.key as FocusedActivationKey },
        location
      );
    }
    return drillOrNoop({ account, structural, statusLabel: data.name ?? '' }, location);
  }

  if (data.type === 'reason') {
    // Overlay reason on the seeded+deepened structural location (ancestry may
    // omit peeled parents; current location supplies them — §4.3).
    return drillOrNoop(
      {
        account,
        structural,
        focusedActivationKey: focusedActivationKeyForNode(node) ?? location.focusedActivationKey,
        reasonLabel: data.name ?? '',
      },
      location
    );
  }

  if (!DRILLABLE_NODE_TYPES.has(data.type)) return { type: 'noop' };
  return drillOrNoop({ account, structural }, location);
}
